import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { SidebarNavItem, SidebarNavSegment } from '@/config/sidebarNav'

function navLinkClass({ isActive }: { isActive: boolean }) {
  return cn(
    'relative flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg py-2.5 pl-3 pr-2 text-sm font-medium transition-all duration-150',
    isActive
      ? 'bg-primary/15 text-foreground shadow-sm ring-1 ring-primary/20 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-0.5 before:rounded-full before:bg-primary'
      : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-foreground',
  )
}

type SidebarNavSectionsProps = {
  segments: SidebarNavSegment[]
  passwordLocked: boolean
  billingLocked: boolean
  contactUnreadTotal: number
  onNavigate?: () => void
}

function isItemNavDisabled(
  item: SidebarNavItem,
  passwordLocked: boolean,
  billingLocked: boolean,
): boolean {
  if (passwordLocked) return true
  if (billingLocked && !item.allowWhenBillingLocked) return true
  return false
}

function NavItemContent({
  item,
  contactUnreadTotal,
}: {
  item: SidebarNavItem
  contactUnreadTotal: number
}) {
  const showBadge = item.badgeKey === 'contact-messages' && contactUnreadTotal > 0
  return (
    <>
      <span className="flex min-w-0 items-center gap-3">
        {item.icon}
        <span className="truncate">{item.label}</span>
      </span>
      {showBadge ? (
        <span
          className="shrink-0 rounded-full bg-destructive px-1.5 py-0.5 text-center text-[10px] font-semibold leading-none text-destructive-foreground"
          aria-label={`${contactUnreadTotal} unread messages`}
        >
          {contactUnreadTotal > 99 ? '99+' : contactUnreadTotal}
        </span>
      ) : (
        <span className="w-5 shrink-0" aria-hidden />
      )}
    </>
  )
}

export function SidebarNavSections({
  segments,
  passwordLocked,
  billingLocked,
  contactUnreadTotal,
  onNavigate,
}: SidebarNavSectionsProps) {
  return (
    <>
      {segments.map((segment, segmentIndex) => (
        <section
          key={segment.title ?? `segment-${segmentIndex}`}
          className={cn(segmentIndex > 0 && 'mt-5 border-t border-sidebar-border/50 pt-4')}
        >
          {segment.title ? (
            <p className="text-muted-foreground mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.14em]">
              {segment.title}
            </p>
          ) : null}
          <div className="flex flex-col gap-0.5">
            {segment.items.map((item) => {
              const disabled = isItemNavDisabled(item, passwordLocked, billingLocked)
              return disabled ? (
                <span
                  key={item.to}
                  title={
                    passwordLocked
                      ? 'Finish setting your password to use navigation'
                      : 'Complete subscription setup to use navigation'
                  }
                  className={cn(navLinkClass({ isActive: false }), 'cursor-not-allowed opacity-50')}
                  aria-disabled="true"
                >
                  <NavItemContent item={item} contactUnreadTotal={contactUnreadTotal} />
                </span>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={navLinkClass}
                  onClick={onNavigate}
                >
                  <NavItemContent item={item} contactUnreadTotal={contactUnreadTotal} />
                </NavLink>
              )
            })}
          </div>
        </section>
      ))}
    </>
  )
}
